import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import translations from '../locales';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Route mappings for different languages
const routeMappings = {
  de: {
    home: '/',
    villa: '/die-villa',
    area: '/die-umgebung',
    arrival: '/deine-anreise',
    conditions: '/bedingungen',
    contact: '/kontakt',
    booking: '/buchung',
    privacy: '/datenschutz',
    imprint: '/impressum',
    download: '/download'
  },
  en: {
    home: '/en',
    villa: '/en/the-villa',
    area: '/en/the-area',
    arrival: '/en/getting-here',
    conditions: '/en/terms',
    contact: '/en/contact',
    booking: '/en/booking',
    privacy: '/en/privacy',
    imprint: '/en/imprint',
    download: '/en/download'
  },
  th: {
    home: '/th',
    villa: '/th/the-villa',
    area: '/th/the-area',
    arrival: '/th/getting-here',
    conditions: '/th/terms',
    contact: '/th/contact',
    booking: '/th/booking',
    privacy: '/th/privacy',
    imprint: '/th/imprint',
    download: '/th/download'
  }
};

// Reverse mapping to get page key from path
const getPageKeyFromPath = (pathname) => {
  // Check German routes
  if (pathname === '/' || pathname === '') return 'home';
  if (pathname === '/die-villa') return 'villa';
  if (pathname === '/die-umgebung') return 'area';
  if (pathname === '/deine-anreise') return 'arrival';
  if (pathname === '/bedingungen') return 'conditions';
  if (pathname === '/kontakt') return 'contact';
  if (pathname === '/buchung') return 'booking';
  if (pathname === '/datenschutz') return 'privacy';
  if (pathname === '/impressum') return 'imprint';
  if (pathname === '/download') return 'download';
  
  // Check English routes
  if (pathname === '/en' || pathname === '/en/') return 'home';
  if (pathname === '/en/the-villa') return 'villa';
  if (pathname === '/en/the-area') return 'area';
  if (pathname === '/en/getting-here') return 'arrival';
  if (pathname === '/en/terms') return 'conditions';
  if (pathname === '/en/contact') return 'contact';
  if (pathname === '/en/booking') return 'booking';
  if (pathname === '/en/privacy') return 'privacy';
  if (pathname === '/en/imprint') return 'imprint';
  if (pathname === '/en/download') return 'download';
  
  // Check Thai routes
  if (pathname === '/th' || pathname === '/th/') return 'home';
  if (pathname === '/th/the-villa') return 'villa';
  if (pathname === '/th/the-area') return 'area';
  if (pathname === '/th/getting-here') return 'arrival';
  if (pathname === '/th/terms') return 'conditions';
  if (pathname === '/th/contact') return 'contact';
  if (pathname === '/th/booking') return 'booking';
  if (pathname === '/th/privacy') return 'privacy';
  if (pathname === '/th/imprint') return 'imprint';
  if (pathname === '/th/download') return 'download';
  
  return 'home'; // Default
};

// Detect browser language and return appropriate language code
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const langCode = browserLang.toLowerCase().split('-')[0];
  
  // German -> de
  if (langCode === 'de') return 'de';
  // Thai -> th
  if (langCode === 'th') return 'th';
  // All other languages -> en
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect language from URL
  const getLanguageFromPath = (pathname) => {
    if (pathname.startsWith('/en')) return 'en';
    if (pathname.startsWith('/th')) return 'th';
    return 'de'; // Default German
  };
  
  const [language, setLanguage] = useState(getLanguageFromPath(location.pathname));
  const [hasAutoDetected, setHasAutoDetected] = useState(false);
  
  // Auto-detect browser language on first visit to root
  useEffect(() => {
    // Only auto-detect if:
    // 1. User is on the root path (/)
    // 2. We haven't auto-detected yet in this session
    // 3. There's no language preference stored
    const storedLang = sessionStorage.getItem('villa-lang-preference');
    
    if (location.pathname === '/' && !hasAutoDetected && !storedLang) {
      const detectedLang = detectBrowserLanguage();
      setHasAutoDetected(true);
      
      // If detected language is not German, redirect
      if (detectedLang !== 'de') {
        const targetPath = routeMappings[detectedLang].home;
        navigate(targetPath, { replace: true });
      }
    }
  }, [location.pathname, hasAutoDetected, navigate]);
  
  useEffect(() => {
    setLanguage(getLanguageFromPath(location.pathname));
  }, [location.pathname]);
  
  // Change language and navigate to new URL
  const changeLanguage = (newLang) => {
    // Store user preference
    sessionStorage.setItem('villa-lang-preference', newLang);
    
    const pageKey = getPageKeyFromPath(location.pathname);
    const newPath = routeMappings[newLang][pageKey] || routeMappings[newLang].home;
    navigate(newPath);
  };
  
  // Translation function
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language] || translations.de;
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to German
        let fallbackValue = translations.de;
        for (const fallbackKey of keys) {
          if (fallbackValue && fallbackValue[fallbackKey] !== undefined) {
            fallbackValue = fallbackValue[fallbackKey];
          } else {
            return key;
          }
        }
        return fallbackValue;
      }
    }
    
    return value;
  };
  
  // Get route mappings for current language
  const getRoutes = () => routeMappings[language];
  
  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, getRoutes, routeMappings }}>
      {children}
    </LanguageContext.Provider>
  );
};
